import { Injectable } from '@angular/core';
import { AuthService } from '@core/services/auth.service';
import { UtilMethods } from '@core/util/util-methods';
import { UmagUserService } from '@core/services/umag--user.service';
import { ProfileService } from '@core/services/profile.service';
import { CountriesHashData, GeoUnitService, MappedCountryData } from '@core/services/geo-unit.service';
import { AppContextService } from '@core/services/app-context.service';
import { GeoUnitType } from '../../models/partners/partner-const-types';
import { ParsePhoneNumberService } from '@core/services/parse-phone-number.service';
import { Permissions } from '@core/util/permissions.enum';
import { AmplitudeService } from './amplitude.service';
import { firstValueFrom } from 'rxjs';
import { defaultGeoUnits } from '@core/country/default-country';
import { CompanyService } from '@core/services/company.service';

@Injectable({
  providedIn: 'root',
})
export class AppInitService {
  public static initGeoInitList: () => Promise<void>;

  constructor(
    private authService: AuthService,
    private umagUserService: UmagUserService,
    private profileService: ProfileService,
    private geoUnitService: GeoUnitService,
    private appContext: AppContextService,
    private phoneParser: ParsePhoneNumberService,
    private amplitudeService: AmplitudeService,
    private companyService: CompanyService
  ) {
    AppInitService.initGeoInitList = this.initAvailableCountries.bind(this);
  }

  initApp(): Promise<any> {
    if (this.authService.isAuthenticated()) {
      const getPermissions = firstValueFrom(this.authService.getPermissions())
        .then((perms) => {
          this.authService.setPermission(UtilMethods.createMapBy<Permissions>(perms));
          if (AuthService.permissions.has(Permissions.OFFICE_ROLES_OBSERVE)) {
            return firstValueFrom(this.umagUserService.userGroupList());
          }
          return [];
        })
        .then((userGroups) => {
          this.umagUserService.userGroups.next(userGroups);
        });

      const getCounties = this.initAvailableCountries();

      const profile = firstValueFrom(this.profileService.getProfile()).then((user) => this.appContext.currentUser.next(user));

      const umagRoot = firstValueFrom(this.companyService.getUmagRoot()).then((root) =>
        localStorage.setItem('rootCompany', JSON.stringify(root))
      );

      return Promise.all([getPermissions, profile, getCounties, umagRoot]).finally(() => this.amplitudeService.init());
    }
  }

  initAvailableCountries(): Promise<void> {
    if (!this.authService.isAuthenticated()) {
      this.appContext.availableCountries.next(
        defaultGeoUnits.map(({ countryCode, id, name, parentId, type }) => ({
          countryCode,
          id,
          name,
          parentId,
          type,
        }))
      );
      return Promise.resolve();
    } else {
      return firstValueFrom(this.geoUnitService.geoUnitList<GeoUnitType.COUNTRY>(GeoUnitType.COUNTRY))
        .then((data) => this.appContext.availableCountries.next(data))
        .then((_) => {
          const availableCountries = this.appContext.availableCountries.value;
          const mappedCountries: MappedCountryData[] = availableCountries.map(({ countryCode, name, id }) => {
            const { code, mask, placeholder } = this.phoneParser.phoneDataByCountryCode(countryCode);
            return { name, countryCode, mask, code, id, placeholder };
          });

          const initResultValue: CountriesHashData = {
            byCode: new Map(),
            byCountry: new Map(),
          };

          const reducer = (result: typeof initResultValue, current: MappedCountryData) => {
            result.byCode.set(Number(current.code), current);
            result.byCountry.set(current.countryCode, current);
            return result;
          };

          const countriesHashMap = mappedCountries.reduce(reducer, initResultValue);

          this.appContext.countriesHashMap = countriesHashMap;
          this.appContext.mappedCountries.next(mappedCountries);
        });
    }
  }
}
